/**
 * usePaymentsConfig Hook
 *
 * This hook contains logic to handle access to the payments configuration (including payment providers config)
 *
 * This hook may be called from React components and hooks
 *
 * Use to access:
 * * Payments config data for current client - loaded from payment service
 * * Check if payment provider is available - based on loaded payments config data & providersOverride values (if set)
 * * Payments config for specific provider - A sub-set of payments config data
 * * Stripe public key for current client - A specific field of Stripe provider config
 * * Braintree/GooglePay mode - A specific field of Braintree/GooglePay provider config
 * * Braintree/GooglePay merchant ID - A specific field of Braintree/GooglePay provider config
 * * Paypal client ID - A specific field of Paypal provider config
 * * Paypal account identifier - A specific field of Paypal provider config
 */

import { useCallback } from 'react';

import {
  useGetPaymentsConfigQuery,
  PaymentsConfigData,
  ProviderTypeConfig,
  StripeProviderConfig,
  BraintreeGooglepayProviderConfig,
  PaypalProviderConfig,
  BraintreeProviderConfig,
} from '@/api/payments/getPaymentsConfig';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { useClient } from '@/services/client/useClient';
import { deviceSupportsApplePay } from '@/providers/braintreeApplepay/deviceSupportsApplePay';

export type DependencyName = keyof PaymentsConfigData['dependencies'];
export type ProviderGroupName = keyof PaymentsConfigData['provider'];

export function usePaymentsConfig() {
  const providersOverride = useAppSelector(state => state.urlOverrides.providersOverride);

  const { getClientId } = useClient();

  const {
    data,
    error,
    isLoading,
  } = useGetPaymentsConfigQuery({
    clientId: getClientId(),
    allProviders: !!providersOverride,
  }, {
    refetchOnMountOrArgChange: 1800, // Refetch every 30 minutes
  });

  const redirectIfActive = useCallback((): void => {
    if (!data?.data?.redirect?.active || !data?.data?.redirect?.url) {
      return;
    }

    logger.info('Redirecting based on payments config...');
    window.location.href = data.data.redirect.url;
  }, [data?.data?.redirect]);

  const isDependencyEnabled = useCallback((dependency: DependencyName): (boolean | null) => {
    if (typeof data?.data?.dependencies !== 'object') {
      return null;
    }
    const dependenciesData = data.data.dependencies;

    if (!(dependency in dependenciesData)) {
      return false;
    }

    return !!dependenciesData[dependency];
  }, [data?.data?.dependencies]);

  const isProviderAvailable = useCallback((groupName: ProviderGroupName, providerName: string): (boolean | null) => {
    if (typeof data?.data?.provider !== 'object' || !(groupName in data.data.provider)) {
      return null;
    }
    const providerGroupData = data.data.provider[groupName];

    if (!(providerName in providerGroupData)) {
      return false;
    }
    if (providersOverride && !providersOverride.includes(providerName)) {
      return false;
    }

    if (providerName === 'BRAINTREE_APPLEPAY' && !deviceSupportsApplePay()) {
      return false;
    }

    return true;
  }, [data?.data?.provider, providersOverride]);

  const getProviderConfig = useCallback((groupName: ProviderGroupName, providerName: string): (ProviderTypeConfig | null) => {
    if (typeof data?.data?.provider !== 'object' || !(groupName in data.data.provider)) {
      return null;
    }
    const providerGroupData = data.data.provider[groupName];

    if (!(providerName in providerGroupData)) {
      return null;
    }
    if (providersOverride && !(providersOverride.includes(providerName))) {
      return null;
    }

    return providerGroupData[providerName as keyof typeof providerGroupData];
  }, [data, providersOverride]);

  const getProviderStripePublicKey = useCallback((): (string | null) => {
    const providerConfig = getProviderConfig('single', 'STRIPE') as StripeProviderConfig;
    if (!providerConfig) {
      return null;
    }

    return providerConfig.public_key;
  }, [getProviderConfig]);

  const getBraintreeGooglepayMode = useCallback((): (string | null) => {
    const providerConfig = getProviderConfig('single', 'BRAINTREE_GOOGLEPAY') as BraintreeGooglepayProviderConfig;
    if (!providerConfig) {
      return null;
    }

    return providerConfig?.mode ?? 'test'; // Mode is optional. Return a default value of "test" so we can tell if config loading is complete.
  }, [getProviderConfig]);

  const getBraintreeGooglepayMerchantId = useCallback((): (string | null) => {
    const providerConfig = getProviderConfig('single', 'BRAINTREE_GOOGLEPAY') as BraintreeGooglepayProviderConfig;
    if (!providerConfig) {
      return null;
    }

    return providerConfig?.google_merchant_id ?? null;
  }, [getProviderConfig]);

  const getProviderPaypalClientId = useCallback((): (string | null) => {
    const providerConfig = getProviderConfig('single', 'PAYPAL') as PaypalProviderConfig;
    if (!providerConfig) {
      return null;
    }

    return providerConfig.client_id;
  }, [getProviderConfig]);

  const getProviderPaypalAccountIdentifier = useCallback((): (string | null) => {
    const providerConfig = getProviderConfig('single', 'PAYPAL') as PaypalProviderConfig;
    if (!providerConfig) {
      return null;
    }

    return providerConfig.account_identifier;
  }, [getProviderConfig]);

  const getProviderStripeAccountIdentifier = useCallback((): (string | null) => {
    const providerConfig = getProviderConfig('single', 'STRIPE') as StripeProviderConfig;
    if (!providerConfig) {
      return null;
    }

    return providerConfig.account_identifier;
  }, [getProviderConfig]);

  const getProviderBraintreeAccountIdentifier = useCallback((): (string | null) => {
    const providerConfig = getProviderConfig('single', 'BRAINTREE') as BraintreeProviderConfig;
    if (!providerConfig) {
      return null;
    }

    return providerConfig.account_identifier;
  }, [getProviderConfig]);

  const getProviderGooglePayAccountIdentifier = useCallback((): (string | null) => {
    const providerConfig = getProviderConfig('single', 'BRAINTREE_GOOGLEPAY') as BraintreeGooglepayProviderConfig;
    if (!providerConfig) {
      return null;
    }

    return providerConfig.account_identifier;
  }, [getProviderConfig]);

  return {
    paymentsConfigData: data,
    paymentsConfigError: error,
    paymentsConfigIsLoading: isLoading,
    redirectIfActive,
    isDependencyEnabled,
    isProviderAvailable,
    getProviderConfig,
    getProviderStripePublicKey,
    getBraintreeGooglepayMode,
    getBraintreeGooglepayMerchantId,
    getProviderPaypalClientId,
    getProviderPaypalAccountIdentifier,
    getProviderStripeAccountIdentifier,
    getProviderBraintreeAccountIdentifier,
    getProviderGooglePayAccountIdentifier,
  };
}
